/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEvent} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import {translate} from '@ion/core/src/locale/locale';
import {UpArrowIcon} from '../../../icons/current/UpArrowIcon';
import {dlsColor} from '../../../theme/theme';
import {IconSize} from '../../../types/types';
import {PulseLoader} from '../../loading/PulseLoader';
import {
  AssetUpdatesControl,
  AssetUpdatesControlProps,
  DEFAULT_UPDATE_PROGRESS
} from './AssetUpdatesControl';

interface AssetListSidePanelHeaderProps {
  label?: string;
  onSort?: (event: MouseEvent) => void;
  sortDescending?: boolean;
  onClose?: (event: MouseEvent) => void;
  updateProgress?: AssetUpdatesControlProps;
}

// the Close icon's geometry ...
const smallIconSize = {width: '16px', height: '16px'};

// only two fixed values are ever needed - hoisted so the render body picks
// between them instead of allocating a fresh object every render ...
const SORT_ICON_ASCENDING  = {transform: 'rotate(0deg)'};
const SORT_ICON_DESCENDING = {transform: 'rotate(180deg)'};

const useStyles = makeStyles(() => ({
  container : {
    display         : 'flex',
    flexDirection   : 'column',
    width           : '100%',
    backgroundColor : dlsColor('white')
  },
  titleRow : {
    boxSizing      : 'border-box',
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'space-between',
    width          : '100%',
    height         : '56px',
    padding        : '0 8px 0 16px',
    gap            : '16px',
    borderBottom   : `2px solid ${dlsColor('pastelTeal')}`,
    borderRadius   : '0 12px 0 0'
  },
  title : {
    fontFamily : 'Inter',
    fontWeight : 600,
    fontSize   : '16px',
    lineHeight : '20px',
    color      : dlsColor('grey1'),
    whiteSpace : 'nowrap',
    flexShrink : 0
  },
  countLabel : {
    fontFamily   : 'Inter',
    fontWeight   : 500,
    fontSize     : '12px',
    lineHeight   : '16px',
    color        : dlsColor('grey2'),
    whiteSpace   : 'nowrap',
    overflow     : 'hidden',
    textOverflow : 'ellipsis',
    textAlign    : 'center',
    flex         : '1 0 0',
    minWidth     : 0
  },
  countLabelLoading : {
    flex     : '1 0 0',
    minWidth : 0
  },
  sortButton : {
    display    : 'flex',
    alignItems : 'center',
    gap        : '8px',
    padding    : 0,
    border     : 'none',
    background : 'none',
    cursor     : 'pointer',
    flexShrink : 0
  },
  sortIcon : {
    display    : 'flex',
    transition : 'transform 0.2s ease'
  },
  sortLabel : {
    fontFamily : 'Inter',
    fontWeight : 500,
    fontSize   : '12px',
    lineHeight : '16px',
    color      : dlsColor('grey1')
  },
  closeButton : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    width          : '44px',
    height         : '44px',
    padding        : 0,
    border         : 'none',
    background     : 'none',
    cursor         : 'pointer',
    flexShrink     : 0
  },
  closeIconBox : {
    boxSizing       : 'border-box',
    display         : 'flex',
    alignItems      : 'center',
    justifyContent  : 'center',
    width           : '24px',
    height          : '24px',
    backgroundColor : dlsColor('white'),
    border          : `0.5px solid ${dlsColor('grey5')}`,
    borderRadius    : '8px'
  }
}));

/**
 * AssetListSidePanelHeader renders the current theme's two-row tracking
 * asset list header, per the Side Panel Header spec: a title row (List
 * View, the "Showing N Assets" count, Sort, and Close) and a data-updates
 * row (a Play/Pause segmented control plus the auto-refresh progress bar).
 * This is a purpose-built replacement for `ResultsModule` in this one
 * context - `ResultsModule` itself is shared by many other grids across the
 * app and is left untouched.
 *
 * ```typescript
 *
 *   import {AssetListSidePanelHeader} from
 *     '@ion/dls/src/components/sidepanel/current/AssetListSidePanelHeader';
 *   ...
 *
 *   <AssetListSidePanelHeader label="Showing 12 Assets"
 *     onSort={onSort}
 *     sortDescending={isAssetsSortDescending}
 *     onClose={onClose}
 *     updateProgress={{
 *       isAssetUpdatesEnabled,
 *       isDataLoading: isRefreshFetching,
 *       onAssetUpdatesPlayback,
 *       onProgressCycleComplete,
 *       updateCycleKey
 *     }} />
 *
 * ```
 *
 */
export const AssetListSidePanelHeader = ({
  label,
  onSort,
  sortDescending = false,
  onClose,
  updateProgress = DEFAULT_UPDATE_PROGRESS
}: AssetListSidePanelHeaderProps) => {
  const classes       = useStyles();
  const sortIconStyle = sortDescending ? SORT_ICON_ASCENDING : SORT_ICON_DESCENDING;

  return (
    <div className={classes.container}>
      <div className={classes.titleRow}>
        <span className={classes.title}>
          {translate('common:sidePanelHeader.listViewTitle')}
        </span>
        {label !== 'loading' ? (
          <span className={classes.countLabel}>
            {label || null}
          </span>
        ) : (
          <div className={classes.countLabelLoading}>
            <PulseLoader />
          </div>
        )}
        <button className={classes.sortButton} onClick={onSort} type='button'>
          <span className={classes.sortIcon} style={sortIconStyle}>
            <UpArrowIcon size={IconSize.medium} color={dlsColor('grey1')} />
          </span>
          <span className={classes.sortLabel}>
            {translate('common:sidePanelHeader.sort')}
          </span>
        </button>
        <button className={classes.closeButton} onClick={onClose} type='button'>
          <div className={classes.closeIconBox}>
            <CloseIcon style={{...smallIconSize, color: dlsColor('grey1')}} />
          </div>
        </button>
      </div>
      <AssetUpdatesControl updateProgress={updateProgress} />
    </div>
  );
};

export default AssetListSidePanelHeader;
